import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import dayjs from "dayjs";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  ArrowLeft,
  Calendar,
  ImagePlus,
  Trash2,
  Edit2,
  Save,
  Plus,
  FileImage,
  FileText,
  Download,
  Upload,
  Camera,
} from "lucide-react";
import { toast } from "sonner";
import { CONSULTATION_TYPES } from "../constants/consultation";
import PhotoLightbox from "../components/PhotoLightbox";
import AppointmentSlotPicker, { slotToScheduledAt } from "../components/AppointmentSlotPicker";
import { fileToBase64, compressImageToBase64 } from "../lib/imageCompression";
import CameraCapture from "../components/CameraCapture";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [apptOpen, setApptOpen] = useState(false);
  const [appt, setAppt] = useState({
    date: new Date().toISOString().slice(0, 10),
    hhmm: "",
    consultation_type: "General consultation",
    fees: 250,
    is_walkin: false,
    reason: "",
    notes: "",
    status: "scheduled",
  });
  const [feeReason, setFeeReason] = useState("");
  const [queue, setQueue] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const fileRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const docFileRef = useRef(null);
  const [docType, setDocType] = useState("");
  const [docNote, setDocNote] = useState("");
  const [docCameraOpen, setDocCameraOpen] = useState(false);

  const load = useCallback(
    () => api.get(`/patients/${id}`).then((r) => setData(r.data)),
    [id]
  );
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Load today's queue for prev/next navigation
    api
      .get("/dashboard/today")
      .then((r) => setQueue(r.data.filter((a) => a.patient_id)))
      .catch((err) => console.error("Failed to load queue", err));
  }, []);

  useEffect(() => {
    if (data?.patient) setForm({ ...data.patient });
  }, [data]);

  // Suggest fee based on last paid visit; re-run whenever the chosen date changes
  useEffect(() => {
    if (!apptOpen || !id) return;
    const chosenDate = appt.date || new Date().toISOString().slice(0, 10);
    api
      .get(`/patients/${id}/suggested-fee`, {
        params: { appointment_date: chosenDate },
      })
      .then((r) => {
        setAppt((prev) => ({
          ...prev,
          fees: r.data.suggested_fee ?? 250,
        }));
        setFeeReason(r.data.reason || "");
      })
      .catch(() => setFeeReason(""));
  }, [apptOpen, id, appt.date]);

  // Reset form fields fresh each time dialog opens
  useEffect(() => {
    if (!apptOpen) return;
    setAppt((prev) => ({
      ...prev,
      date: new Date().toISOString().slice(0, 10),
      hhmm: "",
      reason: "",
      notes: "",
      consultation_type: "General consultation",
      is_walkin: false,
    }));
  }, [apptOpen]);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const { patient, appointments, prescriptions, documents = [] } = data;

  const savePatient = async () => {
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : null };
      delete payload.id;
      delete payload.created_at;
      await api.put(`/patients/${id}`, payload);
      toast.success("Patient updated");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const deletePatient = async () => {
    if (!window.confirm("Delete this patient and all their data?")) return;
    await api.delete(`/patients/${id}`);
    toast.success("Patient deleted");
    navigate("/patients");
  };

  const createAppt = async (e) => {
    e.preventDefault();
    if (!appt.hhmm) {
      toast.error("Pick a time slot");
      return;
    }
    try {
      const scheduled_at = slotToScheduledAt(appt);
      await api.post("/appointments", {
        patient_id: id,
        scheduled_at,
        reason: appt.reason,
        consultation_type: appt.consultation_type,
        fees: appt.fees,
        is_walkin: appt.is_walkin,
        status: "scheduled",
        notes: appt.notes,
      });
      toast.success("Appointment booked");
      setApptOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to book");
    }
  };

  // Suggest fee based on last paid visit whenever the dialog opens
  // (see useEffect above — moved before the early return to preserve hook order)

  const deleteAppt = async (aid) => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${aid}`);
    load();
  };

  const uploadPrescriptionFile = async (file) => {
    setUploading(true);
    try {
      const b64 = file.type?.startsWith("image/")
        ? await compressImageToBase64(file)
        : await fileToBase64(file);
      await api.post("/prescriptions", { patient_id: id, image_base64: b64, caption });
      setCaption("");
      toast.success("Photo uploaded");
      load();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const f of files) {
      await uploadPrescriptionFile(f);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const deletePhoto = async (pid) => {
    if (!window.confirm("Delete this prescription photo?")) return;
    await api.delete(`/prescriptions/${pid}`);
    load();
  };

  const uploadDocumentFile = async (file, meta = {}) => {
    setUploading(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post("/documents", {
        patient_id: id,
        file_base64: b64,
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        doc_type: meta.doc_type ?? docType,
        note: meta.note ?? docNote,
      });
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const f of files) {
      await uploadDocumentFile(f);
    }
    setDocType("");
    setDocNote("");
    if (docFileRef.current) docFileRef.current.value = "";
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${docId}`);
    load();
  };

  const remove = async (id2) => { /* placeholder unused */ };

  const queueIdx = queue.findIndex((a) => a.patient_id === id);
  const prevAppt = queueIdx > 0 ? queue[queueIdx - 1] : null;
  const nextAppt = queueIdx >= 0 && queueIdx < queue.length - 1 ? queue[queueIdx + 1] : null;
  const currentApptToday = queueIdx >= 0 ? queue[queueIdx] : null;

  const setApptStatus = async (apptId, status, goNext = false) => {
    try {
      const current = queue.find((a) => a.id === apptId) || (data?.appointments || []).find((a) => a.id === apptId);
      if (!current) return;
      const payload = {
        patient_id: current.patient_id,
        scheduled_at: current.scheduled_at,
        reason: current.reason,
        consultation_type: current.consultation_type,
        status,
        notes: current.notes,
        fees: current.fees ?? 250,
        is_walkin: current.is_walkin || false,
      };
      await api.put(`/appointments/${apptId}`, payload);
      toast.success(`Marked ${status}`);
      // Refresh queue + patient
      const r = await api.get("/dashboard/today");
      setQueue(r.data.filter((a) => a.patient_id));
      load();
      if (goNext && nextAppt) {
        navigate(`/patients/${nextAppt.patient_id}`);
      }
    } catch {
      toast.error("Status update failed");
    }
  };

  return (
    <div className="space-y-6 fade-up">
      {queueIdx >= 0 && (
        <Card className="p-3 flex items-center gap-3 bg-primary/5 border-primary/20" data-testid="queue-bar">
          <Button
            variant="outline"
            size="sm"
            disabled={!prevAppt}
            onClick={() => prevAppt && navigate(`/patients/${prevAppt.patient_id}`)}
            data-testid="queue-prev"
          >
            ←
          </Button>
          <div className="flex-1 text-sm">
            <span className="font-heading font-semibold">Today&apos;s queue</span>{" "}
            <span className="text-muted-foreground">
              · {queueIdx + 1} / {queue.length} · {currentApptToday?.display_time}
              {currentApptToday?.status && ` · ${currentApptToday.status}`}
            </span>
          </div>
          {currentApptToday && currentApptToday.status !== "completed" && (
            <>
              {currentApptToday.status !== "ongoing" && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setApptStatus(currentApptToday.id, "ongoing")}
                  data-testid="mark-ongoing"
                >
                  Start (ongoing)
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setApptStatus(currentApptToday.id, "completed", true)}
                data-testid="mark-complete-next"
              >
                Complete & next →
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={!nextAppt}
            onClick={() => nextAppt && navigate(`/patients/${nextAppt.patient_id}`)}
            data-testid="queue-next"
          >
            →
          </Button>
        </Card>
      )}
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid="back-btn"
      >
        <ArrowLeft className="size-4" /> Back
      </button>

      <Card className="p-6">
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="size-16 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0 font-heading text-2xl font-bold">
            {patient.name?.[0]}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight">
                  {patient.name}
                </h1>
                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                  <Badge variant="outline">{patient.unique_id}</Badge>
                  {patient.age && <span>{patient.age} yrs</span>}
                  {patient.gender && <span>• {patient.gender}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                {!editing ? (
                  <Button
                    variant="outline"
                    onClick={() => setEditing(true)}
                    data-testid="edit-patient-btn"
                  >
                    <Edit2 className="size-4 mr-2" /> Edit
                  </Button>
                ) : (
                  <Button onClick={savePatient} data-testid="save-patient-btn">
                    <Save className="size-4 mr-2" /> Save
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={deletePatient}
                  data-testid="delete-patient-btn"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>

            {!editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm">
                {patient.phone && <Info label="Phone" value={patient.phone} />}
                {patient.email && <Info label="Email" value={patient.email} />}
                {patient.address && <Info label="Address" value={patient.address} />}
                {patient.medical_history && (
                  <div className="md:col-span-2 mt-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                      Medical history
                    </p>
                    <p className="text-foreground/80 mt-1">{patient.medical_history}</p>
                  </div>
                )}
                {patient.notes && (
                  <div className="md:col-span-2 mt-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                      Notes
                    </p>
                    <p className="text-foreground/80 mt-1">{patient.notes}</p>
                  </div>
                )}
              </div>
            ) : (
              form && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {["name", "unique_id", "age", "gender", "phone", "email", "address"].map((k) => (
                    <div key={k}>
                      <Label className="capitalize">{k.replace("_", " ")}</Label>
                      {k === "gender" ? (
                        <Select
                          value={form.gender || ""}
                          onValueChange={(v) => setForm({ ...form, gender: v })}
                        >
                          <SelectTrigger data-testid="edit-patient-gender" className="w-full">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            {["Male", "Female", "Other"].map((g) => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={form[k] ?? ""}
                          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        />
                      )}
                    </div>
                  ))}
                  <div className="col-span-2">
                    <Label>Medical history</Label>
                    <Textarea
                      rows={3}
                      value={form.medical_history ?? ""}
                      onChange={(e) => setForm({ ...form, medical_history: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={form.notes ?? ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="appointments">
        <TabsList>
          <TabsTrigger value="appointments" data-testid="tab-appointments">
            Appointments ({appointments.length})
          </TabsTrigger>
          <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">
            Prescription Photos ({prescriptions.length})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            Documents ({documents.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="appointments" className="space-y-3 pt-4">
          <div className="flex justify-end">
            <Dialog open={apptOpen} onOpenChange={setApptOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-appointment-btn">
                  <Plus className="size-4 mr-2" /> Book Appointment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[92vh] p-0 gap-0 flex flex-col">
                <DialogHeader className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 shrink-0">
                  <DialogTitle className="font-heading">New appointment</DialogTitle>
                </DialogHeader>
                <form
                  onSubmit={createAppt}
                  className="flex flex-col min-h-0 flex-1"
                  data-testid="appointment-form"
                >
                  <div className="px-4 sm:px-6 pb-4 space-y-3 overflow-y-auto flex-1 min-h-0">
                    <AppointmentSlotPicker
                      value={appt}
                      onChange={(v) => setAppt((prev) => ({ ...prev, ...v }))}
                      defaultDate={appt.date}
                    />
                    {feeReason && (
                      <p
                        className="text-xs text-muted-foreground italic"
                        data-testid="fee-reason"
                      >
                        Fee auto-suggested: {feeReason}
                      </p>
                    )}
                    <div>
                      <Label>Reason / notes on visit</Label>
                      <Input
                        value={appt.reason}
                        onChange={(e) => setAppt({ ...appt, reason: e.target.value })}
                        data-testid="appointment-reason"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        rows={2}
                        value={appt.notes}
                        onChange={(e) => setAppt({ ...appt, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <DialogFooter className="px-4 sm:px-6 py-3 border-t border-border bg-white shrink-0">
                    <Button type="submit" data-testid="appointment-submit">Save appointment</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {appointments.length === 0 ? (
            <Card className="p-8 border-dashed text-center">
              <Calendar className="size-7 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No appointments yet.</p>
            </Card>
          ) : (
            appointments.map((a) => (
              <Card key={a.id} className="p-4">
                <div className="flex items-center gap-4">
                  <div className="text-center w-20 shrink-0">
                    <div className="text-xs uppercase text-muted-foreground">
                      {dayjs(a.scheduled_at).format("MMM")}
                    </div>
                    <div className="font-heading text-2xl font-bold">
                      {dayjs(a.scheduled_at).format("D")}
                    </div>
                    <div className="text-xs">{a.display_time || dayjs(a.scheduled_at).format("h:mm A")}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{a.reason || "Consultation"}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      {a.consultation_type && (
                        <Badge variant="secondary" className="text-[10px]">
                          {a.consultation_type}
                        </Badge>
                      )}
                      {a.source === "online" && (
                        <Badge
                          variant="outline"
                          className="text-[10px] border-secondary text-secondary-foreground"
                          data-testid={`online-badge-${a.id}`}
                        >
                          Online booking
                        </Badge>
                      )}
                      {a.is_walkin && (
                        <Badge variant="outline" className="text-[10px]">
                          Walk-in
                        </Badge>
                      )}
                    </div>
                    {a.notes && (
                      <p className="text-sm text-muted-foreground mt-1">{a.notes}</p>
                    )}
                  </div>
                  <Badge
                    variant={a.status === "scheduled" ? "default" : "secondary"}
                    className="capitalize"
                  >
                    {a.status}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteAppt(a.id)}
                    data-testid={`delete-appointment-${a.id}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="prescriptions" className="space-y-4 pt-4">
          <Card className="p-5 border-dashed">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <Label>Caption (optional)</Label>
                <Input
                  placeholder="e.g. Consultation 12 Feb — Page 1"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleUpload}
                  data-testid="prescription-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCameraOpen(true)}
                  disabled={uploading}
                  data-testid="take-photo-btn"
                >
                  <Camera className="size-4 mr-2" /> Take Photo
                </Button>
                <Button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-prescription-btn"
                >
                  <ImagePlus className="size-4 mr-2" />
                  {uploading ? "Uploading…" : "Upload Photos"}
                </Button>
              </div>
            </div>
          </Card>

          {prescriptions.length === 0 ? (
            <Card className="p-8 border-dashed text-center">
              <FileImage className="size-7 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No prescription photos yet.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {prescriptions.map((p) => (
                <Card key={p.id} className="overflow-hidden group relative" data-testid={`prescription-${p.id}`}>
                  <img
                    src={p.image_base64}
                    alt={p.caption || "prescription"}
                    className="w-full h-44 object-cover cursor-zoom-in"
                    onClick={() => setLightboxIdx(prescriptions.indexOf(p))}
                    data-testid={`prescription-img-${p.id}`}
                  />
                  <div className="p-3">
                    <p className="text-xs text-muted-foreground">
                      {dayjs(p.uploaded_at).format("MMM D, YYYY")}
                    </p>
                    {p.caption && (
                      <p className="text-sm font-medium line-clamp-2">{p.caption}</p>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => deletePhoto(p.id)}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-prescription-${p.id}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="space-y-4 pt-4">
          <Card className="p-5 border-dashed">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Type (optional)</Label>
                <Input
                  placeholder="Lab report, Scan, Notes…"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  data-testid="document-type-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Note (optional)</Label>
                <Input
                  placeholder="Short description"
                  value={docNote}
                  onChange={(e) => setDocNote(e.target.value)}
                />
              </div>
              <div className="md:col-span-3 flex flex-wrap items-center gap-2">
                <input
                  ref={docFileRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleDocUpload}
                  data-testid="document-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDocCameraOpen(true)}
                  disabled={uploading}
                  data-testid="take-document-photo-btn"
                >
                  <Camera className="size-4 mr-2" /> Take Photo
                </Button>
                <Button
                  type="button"
                  onClick={() => docFileRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-document-btn"
                >
                  <Upload className="size-4 mr-2" />
                  {uploading ? "Uploading…" : "Upload Documents"}
                </Button>
                <span className="text-xs text-muted-foreground">
                  PDF or any file. Images auto-compressed to &lt;500 KB.
                </span>
              </div>
            </div>
          </Card>

          {documents.length === 0 ? (
            <Card className="p-8 border-dashed text-center">
              <FileText className="size-7 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No documents uploaded yet.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {documents.map((d) => (
                <Card
                  key={d.id}
                  className="p-4 flex items-center gap-4"
                  data-testid={`document-${d.id}`}
                >
                  <div className="size-10 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0">
                    <FileText className="size-5" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{d.filename}</p>
                    <div className="flex gap-2 text-xs text-muted-foreground mt-0.5">
                      {d.doc_type && <span>{d.doc_type}</span>}
                      <span>{dayjs(d.uploaded_at).format("MMM D, YYYY")}</span>
                      <span className="truncate">{d.mime_type}</span>
                    </div>
                    {d.note && <p className="text-sm mt-1">{d.note}</p>}
                  </div>
                  <a
                    href={d.file_base64}
                    download={d.filename}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    data-testid={`download-document-${d.id}`}
                  >
                    <Download className="size-4" /> Download
                  </a>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteDoc(d.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-document-${d.id}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      {/* Lightbox for prescription photos (with zoom / pan / prev-next) */}
      {lightboxIdx != null && (
        <PhotoLightbox
          photos={prescriptions.map((p) => ({ src: p.image_base64, caption: p.caption }))}
          index={lightboxIdx}
          onChangeIndex={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={uploadPrescriptionFile}
      />
      <CameraCapture
        open={docCameraOpen}
        onClose={() => setDocCameraOpen(false)}
        onCapture={(file) => uploadDocumentFile(file)}
      />
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">{label}</p>
    <p className="text-foreground/90 mt-0.5">{value}</p>
  </div>
);
